import { useState } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ImageUrlEditor, { ImageSet } from './ImageUrlEditor';
import { useLanguage } from '../context/LanguageContext';

interface ImageUrlDrawerProps {
  defaultHeroUrl: string;
  defaultProductUrls: string[];
  onHeroUrlChange: (url: string) => void;
  onProductUrlsChange: (urls: string[]) => void;
  imageSets?: ImageSet[];
  onSaveImageSet?: (imageSet: Omit<ImageSet, 'id'>) => void;
  onUpdateImageSet?: (imageSet: ImageSet) => void;
  onSelectImageSet?: (imageSet: ImageSet) => void;
  onDeleteImageSet?: (imageSetId: string) => void;
  onExportImageSets?: () => void;
  onImportImageSets?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  currentImageSetId?: string | null; // Add this to track currently selected set
}

function ImageUrlDrawer({
  defaultHeroUrl,
  defaultProductUrls,
  onHeroUrlChange,
  onProductUrlsChange,
  imageSets = [],
  onSaveImageSet,
  onUpdateImageSet,
  onSelectImageSet,
  onDeleteImageSet,
  onExportImageSets,
  onImportImageSets,
  currentImageSetId = null
}: ImageUrlDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Fixed vertical toggle button - left side (hidden when drawer is open) */}
      {!isOpen && (
        <button
          onClick={toggleDrawer}
          className="fixed left-0 top-24 z-[100] flex flex-col items-center justify-center bg-green-600 text-white py-4 px-2 rounded-r-lg shadow-lg hover:bg-green-700 transition-all"
        style={{ 
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          height: '180px'
        }}
        aria-label={t('edit_image_sets')}
      >
        <span className="font-medium tracking-wider">{t('edit_image_sets')}</span>
      </button>
      )}

      {/* Drawer panel - left side */}
      <div
        className={`fixed top-0 left-0 h-full w-full sm:w-[500px] bg-gray-800 shadow-xl z-[90] transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } overflow-y-auto`}
      >
        <div className="p-6 pl-10 relative">
          <button 
            onClick={toggleDrawer}
            className="absolute right-4 top-4 text-gray-400 hover:text-white"
            aria-label={t('close')}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          <h2 className="text-xl font-bold text-white mb-2">{t('edit_image_sets')}</h2>
          <p className="text-gray-300 text-sm mb-6">
            {t('image_urls_intro')}
          </p>
          
          <ImageUrlEditor
            defaultHeroUrl={defaultHeroUrl}
            defaultProductUrls={defaultProductUrls}
            onHeroUrlChange={onHeroUrlChange}
            onProductUrlsChange={onProductUrlsChange}
            onSaveImageSet={onSaveImageSet}
            onUpdateImageSet={onUpdateImageSet}
            imageSets={imageSets}
            onSelectImageSet={onSelectImageSet}
            onDeleteImageSet={onDeleteImageSet}
            onExportImageSets={onExportImageSets}
            onImportImageSets={onImportImageSets}
            currentImageSetId={currentImageSetId}
          />
        </div>
      </div>
    </>
  );
}

export default ImageUrlDrawer;